/**
 * Store Current User to Local Storage UseCase
 * 
 * Stores authenticated user data to SecureStorageWebCrypto as UserStorage entity.
 * Follows the same pattern as syncSecureStorageUseCase - converts to storage entity before persisting.
 * This is step 1 before triggering the sync.
 * 
 * Matches Flutter pattern where user is stored locally before sync begins.
 */

import { SecureStorageWebCrypto } from '@/data/storage/local/secureStorageWebCrypto';
import type { UserEntity } from '@/domain/entities/user/userEntity';
import { UserRemapper } from '@/domain/remappers/user/userRemapper';
import { SECURE_STORAGE_KEYS } from '@/domain/entities/secureStorage/secureStorageKeys';
import { appLogger } from '@/domain/services/analytics/appLogger';

export class StoreCurrentUserToLocalStorageUseCase {
  private storage: SecureStorageWebCrypto;

  constructor(storage?: SecureStorageWebCrypto) {
    this.storage = storage || SecureStorageWebCrypto.getInstance();
  }

  /**
   * Store user to local encrypted storage
   * Converts UserEntity to UserStorage before persisting
   * 
   * @param user - The authenticated user entity
   */
  async execute(user: UserEntity): Promise<void> {
    try {
      appLogger.debug('Storing user', 'StoreCurrentUserToLocalStorageUseCase.execute', { context: { firebaseId: user.firebaseId } });

      // Convert entity to storage entity (matching syncSecureStorageUseCase pattern)
      const userStorage = UserRemapper.toStorage(user);

      // Store user storage entity in encrypted storage with a known key
      await this.storage.setEncrypted(SECURE_STORAGE_KEYS.CURRENT_USER, userStorage);

      appLogger.debug('User stored successfully', 'StoreCurrentUserToLocalStorageUseCase.execute');
    } catch (error) {
      appLogger.error('Failed to store user', 'StoreCurrentUserToLocalStorageUseCase.execute', { error: error as Error });
      throw error;
    }
  }
}

// Export singleton instance
export const storeCurrentUserToLocalStorageUseCase = new StoreCurrentUserToLocalStorageUseCase();
